import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { DeleteIcon, ArrowsMoveIcon } from 'src/assets/icons'
import { EditMediaIcon } from 'src/assets/icons/v2/EditMediaIcon'
import { IMediaFileWithId } from 'src/types'

import useStyles from './styles'
import { MediaImageRender } from './media-image-render.component'
import { HelperWrapper } from '../helper-wrapper'
import { MediaImagePreview } from './media-image-previev.component'
import { EyeOpenedIcon } from 'src/assets/icons/v2/EyeOpenedIcons'
import { BarcodeIcon } from 'src/assets/icons/v2/BarcodeIcon'

interface IMediaImageDraggableProps {
  handleRemove?: () => void
  link: string
  alt: string
  handleImageClick?: () => void
  type: string
  title?: IMediaFileWithId['title']
  link_medium?: string | null
  isDisabled?: boolean
  id?: string
  withDnd?: boolean
  scanToEarnMode?: boolean
  isBarcodeImage?: boolean
}

const MediaImageDraggable = ({
  handleRemove,
  link,
  alt,
  handleImageClick,
  type,
  title,
  link_medium,
  isDisabled,
  id,
  withDnd,
  scanToEarnMode = false,
  isBarcodeImage = false,
}: IMediaImageDraggableProps) => {
  const classes = useStyles({ isModalImage: !title })
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: id || link,
  })

  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: isTablet ? '60px' : '100px',
    height: scanToEarnMode ? (isTablet ? '60px' : '100px') : isTablet ? '90px' : '130px',
    zIndex: isDragging ? '100' : 'auto',
  }

  return (
    <div className={classes.imageItemWrapper} style={style} ref={setNodeRef}>
      <div className={classes.imageWrapper}>
        <IconButton className={classes.imageButton}>
          {isBarcodeImage && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%) ',
                left: 0,
                right: 0,
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              <BarcodeIcon opacity={0.5} />
            </Box>
          )}
          <MediaImageRender
            className={`${classes.image} ${isBarcodeImage && classes.imageMasked}`}
            alt={alt}
            link={link_medium || link}
            type={type}
            modalLink={link}
          />
        </IconButton>
        {scanToEarnMode && handleRemove && (
          <IconButton
            aria-label="delete"
            className={classes.removeImageButton}
            onClick={handleRemove}
          >
            {!isDisabled && <DeleteIcon className={classes.removeImage} />}
          </IconButton>
        )}
        {withDnd && !isDisabled && (
          <div className={classes.arrowButtonsWrapper}>
            <IconButton
              className={classes.moveImageButton}
              {...listeners}
              {...attributes}
              aria-label="move"
            >
              {!isDisabled && <ArrowsMoveIcon size={28} />}
            </IconButton>
          </div>
        )}
        {!scanToEarnMode && (
          <IconButton
            className={classes.editImageButton}
            aria-label="edit"
            disabled={!handleImageClick || isDisabled}
            onClick={handleImageClick}
          >
            {!isDisabled && <EditMediaIcon size={32} />}
          </IconButton>
        )}
        {!scanToEarnMode && (
          <IconButton
            className={classes.openImageButton}
            aria-label="open-preview"
            disabled={isDisabled}
          >
            {!isDisabled && (
              <MediaImagePreview link={link} type={type}>
                <EyeOpenedIcon size={32} />
              </MediaImagePreview>
            )}
          </IconButton>
        )}
      </div>
      {/* {title && (
        <div className={classes.imageTitleWrapper}>
          <Typography className={classes.imageTitle}>{title}</Typography>
        </div>
      )} */}
      {!scanToEarnMode && (
        <div className={classes.imageTitleWrapper}>
          <Typography
            className={classes.imageTitle}
            sx={{ color: !!title ? 'inherit' : theme.palette.custom.warning }}
          >
            {title || <HelperWrapper t="media-title-required-error" isColorInherited />}
          </Typography>
        </div>
      )}
    </div>
  )
}

export { MediaImageDraggable }
